/**
 * The read-side correction for D1 TEXT timestamps: `datetime('now')` writes UTC and says so nowhere, so every reader
 * that hands the raw column to `new Date(...)` gets the viewer's LOCAL interpretation of a UTC wall clock.
 *
 * THE DEFECT. `SQLITE_NOW_SQL` (`const/sql-fragments.ts`) is `datetime('now')`, which emits `2026-08-14 02:15:33` --
 * space-separated, no `Z`, no offset. That shape is not ISO-8601, so V8 falls back to its implementation-defined
 * parse and reads it as local time. A row written seconds ago rendered as "3h ago" for a reader at UTC+3.
 *
 * WHY THE FIX IS HERE AND NOT AT STORAGE. Changing what `SQLITE_NOW_SQL` writes would correct only rows written after
 * the change and leave every existing row in the old shape, so two formats would be live at once and every reader
 * would need to handle both anyway. Normalizing on READ corrects rows already written and rows still to come with one
 * decision.
 *
 * WHY `const/`, NOT `utils/` OR `tools/`. The readers span every layer -- `formatRelative` in `components/`,
 * `formatNotificationTime` in `utils/dates/`, and `parseDate` / `formatTimeAgo`, which sat in `utils/dates/` and
 * `tools/dates/` respectively until the date family consolidated into `const/date-parse-format.ts` and
 * `const/time-ago.ts` on 2026-08-20 for this same reason -- and no other layer
 * is reachable from all of them. A `utils/` leaf may value-import ONLY `@federa/const`, `@federa/types` and `zod`
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist), which forecloses a `utils/` home; a `tools/` leaf may not
 * import a `tools/` sibling (`QUAL-NO-CROSS-TOOL-IMPORTS-001`), which forecloses a `tools/` home. `const/` is the one
 * layer all three already reach, and it already hosts pure behaviour of exactly this size (`const/epoch-date.ts`,
 * `const/json-parse.ts`, `const/record-guards.ts`).
 */

/**
 * The SQLite `datetime('now')` wall-clock shape: `YYYY-MM-DD HH:MM:SS`, optional fractional seconds, no zone marker.
 *
 * DELIBERATELY SPACE-SEPARATED, AND ANCHORED AT BOTH ENDS. The `T`-separated zone-less form (`2026-08-14T02:15:33`)
 * is excluded on purpose -- this repo hand-builds that form where LOCAL midnight is the intent (the explore
 * calendar's `new Date(\`${from}T00:00:00\`)`), so widening the pattern would shift those pickers by the viewer's
 * offset. A trailing `Z` or `+HH:MM` fails the end anchor, which is what stops an already-zoned value being
 * re-stamped.
 *
 * EXPORTED because it is a DERIVED decision, not a coincidence: this shape is a property of the ONE producer,
 * `SQLITE_NOW_SQL` (`const/sql-fragments.ts`), and `const/w3c-datetime.ts` reads this rather than a private copy.
 * The day D1 emits a different shape -- a `T` separator, a default milliseconds field -- BOTH recognizers must move or
 * the one left behind stops recognizing rows and silently passes them through: the read-side normalizer would hand
 * `new Date` a naive string again, and the sitemap coercer would drop `<lastmod>` on every row. One shape, one pattern.
 *
 * The DATE and TIME halves are capture groups for the coercer, which rebuilds the value as `<date>T<time>Z`; the
 * `.test`-only consumer below is indifferent to them. No `/g` or `/y` flag, so the shared instance carries no
 * `lastIndex` between the two consumers.
 */
export const SQLITE_NAIVE_UTC_DATETIME_PATTERN = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/;

/**
 * Stamp the missing `Z` onto a D1 wall-clock timestamp; pass EVERYTHING ELSE through byte-for-byte.
 *
 * Non-strings return untouched -- a `Date` or an epoch number carries no ambiguity to resolve -- and so does any
 * string already bearing a zone, any `T`-separated value, and any garbage. The narrow pattern is the whole safety
 * property: blindly appending `Z` would corrupt the proper-ISO values this codebase also stores.
 *
 * @runtime-derived-ssot-helper: the gate is the same-leaf `SQLITE_NAIVE_UTC_DATETIME_PATTERN` SSOT that
 * `const/w3c-datetime.ts` reads, so what counts as a naive D1 timestamp is one decision across both readers. Its
 * consumers include pure `utils/dates/` leaves, which may not value-import a `utils/` sibling, so the const floor is
 * the only home they can all reach.
 */
export function normalizeSqliteUtcTimestamp<T>(value: T): T | string {
	if (typeof value !== 'string') return value;
	const trimmed = value.trim();
	if (!SQLITE_NAIVE_UTC_DATETIME_PATTERN.test(trimmed)) return value;
	return `${trimmed.replace(' ', 'T')}Z`;
}
